// ─────────────────────────────────────────────────────────────────────────────
// SITE THEME → CSS
//
// One place that turns site.themeConfig (written by ThemePicker, Settings and —
// most importantly — the AI design_brief) into CSS. Used by BOTH the published
// renderer and the editor canvas, so the editor shows the real site styling.
//
// Two layers:
//  1. Legacy layer (moved verbatim from apps/renderer): --site-* variables,
//     body/heading fonts and the attribute-selector colour override hack.
//  2. Design tokens --t-*: the AI design system in a namespaced form. Blocks in
//     render/blockHtml.ts read them as `var(--t-bg, <old default>)`, so a site
//     without a theme renders exactly as before, and an AI site gets its palette,
//     fonts, radii and type scale everywhere.
//
// Tokens are namespaced (--t-*) on purpose: the editor UI itself uses --accent,
// --border, --text-muted… on :root, and AI pageCss defines the same names.
// ─────────────────────────────────────────────────────────────────────────────

export interface SiteThemeConfig {
  primaryColor?: string; secondaryColor?: string; bgColor?: string; textColor?: string
  borderRadius?: number; fontHeading?: string; fontBody?: string; customVars?: string
  // Extended tokens from AI design_brief
  surface?: string; bgAlt?: string; accentMuted?: string; accentLight?: string; textMuted?: string; border?: string
  radius?: { sm?: string; md?: string; lg?: string; xl?: string }
  shadows?: { sm?: string; md?: string; lg?: string }
  typography?: {
    displaySize?: string; h1Size?: string; h2Size?: string; h3Size?: string; bodySize?: string
    headingWeight?: string; letterSpacing?: string; lineHeightHeading?: string; lineHeightBody?: string
  }
  buttons?: { radius?: string; height?: string; paddingX?: string }
  /** 'v2' = core blocks rendered from design tokens (set when an AI generation is applied). */
  designSystem?: string
}

export function parseThemeConfig(raw: unknown): SiteThemeConfig | null {
  if (!raw) return null
  if (typeof raw === 'object') return raw as SiteThemeConfig
  try { return JSON.parse(String(raw)) as SiteThemeConfig } catch { return null }
}

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

/** Perceived luminance 0..255 for a hex colour; null if not a hex. */
export function hexLuminance(hex?: string): number | null {
  if (!hex || !HEX_RE.test(hex.trim())) return null
  let h = hex.trim().slice(1)
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000
}

function clean(v: unknown): string {
  // Keep values safe inside a <style> tag
  return String(v ?? '').replace(/[<>{};]/g, '').trim()
}

/** Font families in a CSS font-family list that should be loaded from Google Fonts. */
export function googleFontFamilies(t: SiteThemeConfig | null): string[] {
  if (!t) return []
  const SYSTEM = /^(serif|sans-serif|monospace|cursive|system-ui|-apple-system|inherit|arial|helvetica|georgia|times new roman|blinkmacsystemfont|segoe ui)$/i
  const out = new Set<string>()
  for (const list of [t.fontHeading, t.fontBody]) {
    const first = String(list ?? '').split(',')[0]?.replace(/["']/g, '').trim()
    if (first && !SYSTEM.test(first) && /^[A-Za-z0-9 ]{2,40}$/.test(first)) out.add(first)
  }
  return [...out]
}

export function googleFontsHref(t: SiteThemeConfig | null): string | null {
  const fams = googleFontFamilies(t)
  if (!fams.length) return null
  return `https://fonts.googleapis.com/css2?${fams.map(f => `family=${encodeURIComponent(f).replace(/%20/g, '+')}:wght@300;400;500;600;700;800`).join('&')}&display=swap`
}

/** --t-* design tokens + base typography, scoped to `scope` (":root" on the site, a class in the editor). */
export function buildDesignTokensCss(t: SiteThemeConfig | null, scope = ':root'): string {
  if (!t) return ''
  const v: string[] = []
  const set = (name: string, val: unknown) => { const c = clean(val); if (c) v.push(`${name}:${c}`) }

  const bg = t.bgColor
  const bgLum = hexLuminance(bg)
  const dark = bgLum !== null ? bgLum < 128 : false
  set('--t-bg', bg)
  set('--t-bg-alt', t.bgAlt ?? t.surface)
  set('--t-surface', t.surface)
  set('--t-text', t.textColor)
  set('--t-muted', t.textMuted)
  set('--t-border', t.border)
  set('--t-accent', t.primaryColor)
  set('--t-accent-2', t.accentMuted ?? t.secondaryColor)
  set('--t-accent-soft', t.accentLight)
  const accLum = hexLuminance(t.primaryColor)
  if (accLum !== null) set('--t-on-accent', accLum > 150 ? '#111111' : '#ffffff')
  if (bgLum !== null) set('--t-scheme', dark ? 'dark' : 'light')
  // ink = the darkest of bg/text, paper = the lightest — used for inverted
  // sections (footer, dark tone) so they work on light AND dark palettes.
  if (bg && t.textColor) {
    set('--t-ink', dark ? bg : t.textColor)
    set('--t-paper', dark ? t.textColor : bg)
  }
  set('--t-font-h', t.fontHeading)
  set('--t-font-b', t.fontBody)
  set('--t-radius-sm', t.radius?.sm)
  set('--t-radius', t.radius?.md ?? (t.borderRadius !== undefined ? `${t.borderRadius}px` : undefined))
  set('--t-radius-lg', t.radius?.lg)
  set('--t-radius-xl', t.radius?.xl)
  set('--t-btn-radius', t.buttons?.radius ?? t.radius?.md)
  set('--t-shadow-sm', t.shadows?.sm)
  set('--t-shadow', t.shadows?.md)
  set('--t-shadow-lg', t.shadows?.lg)
  set('--t-h1', t.typography?.displaySize ?? t.typography?.h1Size)
  set('--t-h2', t.typography?.h2Size)
  set('--t-h3', t.typography?.h3Size)
  set('--t-body', t.typography?.bodySize)
  set('--t-h-weight', t.typography?.headingWeight)
  set('--t-h-tracking', t.typography?.letterSpacing)
  set('--t-h-leading', t.typography?.lineHeightHeading)
  if (!v.length) return ''

  const s = scope
  let css = `${s}{${v.join(';')}}`
  // Base typography only for design system v2 — legacy sites keep their exact look
  // (their heading weights/fonts come from the legacy layer and inline styles).
  if (t.designSystem !== 'v2') return css
  css += `${s}{font-family:var(--t-font-b,inherit)}`
  css += `${s} h1,${s} h2,${s} h3,${s} h4,${s} h5,${s} h6{font-family:var(--t-font-h,inherit)}`
  if (t.typography?.letterSpacing) css += `${s} h1,${s} h2{letter-spacing:var(--t-h-tracking)}`
  if (t.typography?.headingWeight) css += `${s} h1,${s} h2,${s} h3{font-weight:var(--t-h-weight)!important}`
  // Inputs/buttons don't inherit font by default
  css += `${s} button,${s} input,${s} select,${s} textarea{font-family:inherit}`
  return css
}

/**
 * Full theme CSS for a published site (legacy override layer + design tokens).
 * Moved from apps/renderer/src/lib/renderSitePage.tsx; behaviour of the legacy
 * part is unchanged.
 */
export function buildSiteThemeCss(themeConfig: unknown, scope = ':root'): string {
  const t = parseThemeConfig(themeConfig)
  if (!t) return ''
  let themeCSS = ''
  try {
    const vars: string[] = []
    if (t.primaryColor) vars.push(`--site-primary:${t.primaryColor}`)
    if (t.secondaryColor) vars.push(`--site-secondary:${t.secondaryColor}`)
    if (t.bgColor) vars.push(`--site-bg:${t.bgColor}`)
    if (t.textColor) vars.push(`--site-text:${t.textColor}`)
    if (t.borderRadius !== undefined) vars.push(`--site-radius:${t.borderRadius}px`)
    if (t.fontHeading) vars.push(`--site-font-heading:${t.fontHeading}`)
    if (t.fontBody) vars.push(`--site-font-body:${t.fontBody}`)
    if (t.surface) vars.push(`--site-surface:${t.surface}`)
    if (t.accentMuted) vars.push(`--site-accent-muted:${t.accentMuted}`)
    if (t.textMuted) vars.push(`--site-text-muted:${t.textMuted}`)
    if (t.border) vars.push(`--site-border:${t.border}`)
    if (t.radius?.sm) vars.push(`--site-radius-sm:${t.radius.sm}`)
    if (t.radius?.md) vars.push(`--site-radius-md:${t.radius.md}`)
    if (t.radius?.lg) vars.push(`--site-radius-lg:${t.radius.lg}`)
    if (t.radius?.xl) vars.push(`--site-radius-xl:${t.radius.xl}`)
    if (t.shadows?.sm) vars.push(`--site-shadow-sm:${t.shadows.sm}`)
    if (t.shadows?.md) vars.push(`--site-shadow-md:${t.shadows.md}`)
    if (t.shadows?.lg) vars.push(`--site-shadow-lg:${t.shadows.lg}`)
    if (t.typography?.h1Size) vars.push(`--site-size-h1:${t.typography.h1Size}`)
    if (t.typography?.h2Size) vars.push(`--site-size-h2:${t.typography.h2Size}`)
    if (t.typography?.h3Size) vars.push(`--site-size-h3:${t.typography.h3Size}`)
    if (t.typography?.bodySize) vars.push(`--site-size-body:${t.typography.bodySize}`)

    const isRoot = scope === ':root'
    const bodySel = isRoot ? 'body' : scope
    const hSel = (h: string) => isRoot ? h : `${scope} ${h}`
    themeCSS = `${scope}{${vars.join(';')}}${bodySel}{background:var(--site-bg,#fff);color:var(--site-text,#111);font-family:var(--site-font-body,-apple-system,sans-serif)}${['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map(hSel).join(',')}{font-family:var(--site-font-heading,-apple-system,sans-serif)}`

    // Inline-style override via CSS attribute selectors (!important beats inline).
    const pre = isRoot ? '' : `${scope} `
    const colorOverride = (hex: string, cssVar: string): string => {
      const lo = hex.toLowerCase()
      const hi = hex.toUpperCase()
      const targets = lo === hi ? [lo] : [lo, hi]
      let css = ''
      for (const P of targets) {
        css += `${pre}[style*="background:${P}"],${pre}[style*="background: ${P}"],${pre}[style*="background-color:${P}"],${pre}[style*="background-color: ${P}"]{background-color:var(${cssVar})!important;background-image:none!important}`
        css += `${pre}[style*="color:${P}"],${pre}[style*="color: ${P}"]{color:var(${cssVar})!important}`
        css += `${pre}[style*="border-color:${P}"],${pre}[style*="border-color: ${P}"],${pre}[style*="border:${P}"],${pre}[style*="border: ${P}"]{border-color:var(${cssVar})!important}`
        css += `${pre}[style*="fill:${P}"]{fill:var(${cssVar})!important}`
        css += `${pre}[style*="stroke:${P}"]{stroke:var(${cssVar})!important}`
        css += `${pre}[style*="accent-color:${P}"]{accent-color:var(${cssVar})!important}`
        css += `${pre}[style*="box-shadow"][style*="${P}"]{box-shadow:0 0 0 3px var(${cssVar})!important}`
        css += `${pre}[style*="linear-gradient"][style*="${P}"]{background-image:linear-gradient(135deg,var(${cssVar}),var(--site-secondary,#8b5cf6))!important;background-color:transparent!important}`
      }
      return css
    }

    if (t.primaryColor) {
      const primaries = [...new Set(['#5b6af0', t.primaryColor.toLowerCase(), t.primaryColor.toUpperCase()])]
      for (const P of primaries) themeCSS += colorOverride(P, '--site-primary')
    }
    if (t.secondaryColor) {
      const secondaries = [...new Set(['#8b5cf6', t.secondaryColor.toLowerCase(), t.secondaryColor.toUpperCase()])]
      for (const S of secondaries) {
        themeCSS += `${pre}[style*="background:${S}"],${pre}[style*="background-color:${S}"]{background-color:var(--site-secondary)!important;background-image:none!important}`
        themeCSS += `${pre}[style*="color:${S}"]{color:var(--site-secondary)!important}`
      }
    }
    if (t.customVars && t.customVars.trim()) {
      themeCSS += `${scope}{${t.customVars}}`
    }
  } catch { /* ignore */ }
  return themeCSS + buildDesignTokensCss(t, scope)
}

/** True when the site opted into design-system-driven core blocks (v2). */
export function isDesignSystemV2(themeConfig: unknown): boolean {
  return parseThemeConfig(themeConfig)?.designSystem === 'v2'
}
